/**
 * Group E — the moderation inbox and its two clears, for both apps.
 *
 * The routes and the wire→prop conversion lived in the web app and the Helper had
 * its own pair on the FROZEN `/admin/moderation/*` twins, with its own copy of the
 * item shape. One client now, over `host.apiGet`/`host.apiSend`, because the two
 * surfaces call the same routes for the same shapes and differ only in what they
 * put on the request.
 *
 * A moderation REPLY is not here: doc 04 §4.7 makes it an outbox row, so the write
 * is `POST …/outbox/items` — see `./outbox`. This module drafts; that one queues.
 *
 * Neither is the BULK clear (`POST …/items/actions/clear`), and that is a deletion
 * rather than an omission: it was scoped BY the inbox filters, the filters are gone,
 * and an unscoped "clear the whole backlog" is not something either surface wants one
 * click away from a per-row action. The route is untouched; `git log` has the client.
 *
 * TWO CONTRACT DETAILS THAT BITE IF YOU MISS THEM
 *
 *  1. The inbox is CURSOR-paginated: `?offset=` is a 400. `next_cursor` is opaque —
 *     pass it back as `cursor`.
 *  2. The cursor is bound to the filter set, `since` included — the server hashes
 *     `(kind, reason, q, since)` into it and answers 400 `cursor_filter_mismatch` if a
 *     later page disagrees. A relative `last=7d` USED TO make every second page a 400,
 *     because it resolved to `now()` with microseconds and the hash moved between two
 *     requests a millisecond apart; #170 rounds it down to the top of the hour, so
 *     relative windows now page. An explicit `since` you resolve once is still fine.
 *  3. `stats` and `total` are `?include=` blocks and they dominate the cost — measured
 *     at 20.5s and 9.5s against a 9.0s page on the largest community. A caller that
 *     walks rows should pass `include: []` and ask for stats separately.
 */

import type { CommunityRef, ListEnvelope } from './envelope';

import { api } from '../transport';
import type {
  CommentReason,
  ImageMedia,
  ModerationItem,
  VideoMedia,
} from './shapes';

/** What `host.apiGet`/`host.apiSend` take. */
type Params = Record<string, string | number | string[]>;

/** The same, before the nulls are dropped — a filter is naturally "this or nothing",
 *  and writing that out at each field reads better than an object built by mutation. */
type Filled = Record<string, string | number | null | undefined>;

/** Drop nulls: every parameter here means "no filter" when absent, and the two apps'
 *  clients skip `undefined` but would serialise a `null` as the string "null". */
function params(p: Filled): Params {
  const out: Params = {};
  for (const [k, v] of Object.entries(p)) if (v !== null && v !== undefined) out[k] = v;
  return out;
}

/**
 * The sidebar counters a write recomputes, so nothing has to re-read to find out.
 *
 * NAMED, not `Record<string, number>`. It was the loose form here and the named form
 * in the web app, which meant the one surface that actually renders these could not
 * be handed the other's value — the typechecker caught it the moment they met.
 * The Helper ignores them; that is a reason to leave a field unread, not to leave a
 * shape undescribed.
 */
export interface Badges {
  inbox: number;
  outbox: number;
}


/** §1.6: the discriminator is `kind`, singular, with the values the emitted per-item
 *  `kind` already uses. The legacy surface spelled it `type=all|posts|comments`. */
export type InboxKind = 'post' | 'comment';

/** v1's `MemberRef` — five keys, one definition. `handle` is nullable here where
 *  `PostAuthor` (the component prop) declares it a string; `toAuthor` bridges that. */
interface MemberRefWire {
  member_id: number;
  external_id: string;
  handle: string | null;
  name: string;
  picture_profile: string | null;
}

interface InboxItemWire {
  kind: InboxKind;
  id: number;
  external_id?: string | null;
  post_id?: number | null;
  parent_id?: number | null;
  depth?: number | null;
  pinned?: boolean | null;
  title?: string | null;
  post_title?: string | null;
  excerpt?: string | null;
  /** Declared by the route, NOT in its default field set (§5.4). Never requested. */
  content?: string | null;
  author?: MemberRefWire | null;
  images?: unknown[] | null;
  videos?: unknown[] | null;
  upvotes?: number | null;
  comment_count?: number | null;
  reply_count?: number | null;
  created_at?: string | null;
  post_created_at?: string | null;
  reason?: string | null;
  url?: string | null;
  draft?: string | null;
  /** In the route's DEFAULT field set, so nothing has to ask for them — see
   *  `ModerationItem`. Optional here only because `?fields=` could drop them; this
   *  client never sends one. */
  group_external_id?: string | null;
  root_external_id?: string | null;
  parent_external_id?: string | null;
}

interface ModerationItemWire extends InboxItemWire {
  cleared: boolean;
  /** ABSENT, not null, when declined via `?include=` (#173/#170's convention). */
  badges?: Badges;
}



// ---------------------------------------------------------------------------
// The filter
// ---------------------------------------------------------------------------

/**
 * The inbox's filter — the same object the GET takes as query parameters and the
 * bulk clear takes as its BODY, because the bulk operation is *defined by* the
 * filter (§1.1: one licensed verb, `POST …/actions/clear`).
 *
 * `since` and `last` are two spellings of one bound and sending both is a 400. See
 * the module note: resolve a relative window to a fixed `since` once, or the cursor
 * stops matching between pages.
 */
// ---------------------------------------------------------------------------
// Wire → component props
// ---------------------------------------------------------------------------

/** `MemberRef` → the `PostAuthor`-compatible author the shared cards render.
 *  `location` and `role` are on the legacy `Moderator` shape and are not on v1's
 *  `MemberRef`; nothing in the inbox renders either, so they are null. */
function toAuthor(a: MemberRefWire | null | undefined): ModerationItem['author'] {
  if (!a) return null;
  return {
    member_id: a.member_id,
    external_id: a.external_id,
    handle: a.handle ?? '',
    name: a.name,
    picture_profile: a.picture_profile,
    location: null,
    role: null,
  };
}

function toItem(w: InboxItemWire): ModerationItem {
  return {
    kind: w.kind,
    id: w.id,
    external_id: w.external_id ?? '',
    // Always present on the wire; a post's is its own id, which is what the drawer
    // fetches the thread with.
    post_id: w.post_id ?? w.id,
    title: w.title ?? null,
    post_title: w.post_title ?? null,
    draft: w.draft ?? null,
    excerpt: w.excerpt ?? '',
    images: (w.images ?? null) as ImageMedia[] | null,
    videos: (w.videos ?? null) as VideoMedia[] | null,
    author: toAuthor(w.author),
    upvotes: w.upvotes ?? null,
    comment_count: w.comment_count ?? null,
    reply_count: w.reply_count ?? null,
    created_at: w.created_at ?? null,
    post_created_at: w.post_created_at ?? null,
    reason: (w.reason ?? 'post') as ModerationItem['reason'],
    url: w.url ?? null,
    // Null TOGETHER when the server could not resolve them, which is not an error:
    // the row still renders and still clears, and only the Helper's post-straight-to-
    // Skool shortcut is unavailable. Never partially filled — a half set would let a
    // caller build a malformed post.
    group_external_id: w.group_external_id ?? null,
    root_external_id: w.root_external_id ?? null,
    parent_external_id: w.parent_external_id ?? null,
  };
}

// ---------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The queue (#171) — one row per POST
// ---------------------------------------------------------------------------

/**
 * The three Skool ids a reply to a row has to be filed under — ALL THREE NULL
 * TOGETHER when it cannot be addressed.
 *
 * NOT duplicated from the thread, which is what the queue's first cut assumed and got
 * wrong: `…/posts/{id}` carries an `external_id` per post and comment, but
 * `groupExternalId` is the community's Skool id and no v1 response exposes it, and
 * `parentExternalId` is the HOISTED top-level ancestor — the one value a client must
 * never compute, because Skool accepts a `parent_id` pointing at a nested comment,
 * echoes it back with an id, then silently drops the comment (#140, PR #144).
 *
 * Present means the Helper can post straight to Skool. Null means it queues instead,
 * which is correct and slower.
 */
export interface Addressing {
  groupExternalId: string | null;
  rootExternalId: string | null;
  parentExternalId: string | null;
}

/** One comment on a queued post still awaiting a reply. The deck walks these ids,
 *  shows the badge, and posts the reply straight to Skool when `addressing` is set. */
export interface QueueWaiting {
  commentId: number;
  reason: CommentReason;
  addressing: Addressing;
}

/**
 * One QUEUED POST — the unit a card is rendered from.
 *
 * DELIBERATELY LEAN: no author, excerpt, media or Skool addressing ids, because the
 * card fetches `…/posts/{postId}` for the thread and every one of those would arrive
 * twice. `waiting` is the part no client can derive.
 *
 * `postNeedsReply` and `waiting` are separate work — answering the post, versus
 * answering somebody in it — and one post can need both.
 */
export interface QueuePost {
  postId: number;
  postCreatedAt: string | null;
  url: string | null;
  postNeedsReply: boolean;
  /** For answering the POST itself. Each `waiting` entry carries its own. */
  addressing: Addressing;
  waiting: QueueWaiting[];
}

export interface QueuePage {
  items: QueuePost[];
  /** Opaque. Pass back as `cursor`; null means this was the last page. */
  nextCursor: string | null;
  /** One number per requested bound. Null when `counts` was not asked for — a
   *  declined block is ABSENT on the wire, and inventing a zero here would be a claim
   *  about a backlog nobody counted. */
  counts: Record<string, number> | null;
  /** The last thing the panel needed the inbox route's `stats` for: the gate that
   *  decides whether this screen renders at all. */
  hasModerators: boolean;
  community: CommunityRef;
}

interface AddressingWire {
  group_external_id?: string | null;
  root_external_id?: string | null;
  parent_external_id?: string | null;
}
interface QueueWaitingWire {
  comment_id: number;
  reason: string;
  addressing?: AddressingWire;
}
interface QueueItemWire {
  post_id: number;
  post_created_at?: string | null;
  url?: string | null;
  post_needs_reply: boolean;
  addressing?: AddressingWire;
  waiting?: QueueWaitingWire[];
}
interface QueuePageWire extends Omit<ListEnvelope<QueueItemWire>, 'total'> {
  community: CommunityRef;
  counts?: Record<string, number>;
  has_moderators: boolean;
}

/**
 * `GET /v1/communities/{skool_id}/moderation/queue` — the newest-first queue of posts
 * that nobody has answered, one row per post (#171).
 *
 * WHY NOT `fetchInbox`: that route returns a union of post rows and comment rows, so a
 * client had to regroup a PAGE into posts — which only groups what the page contains,
 * and can only sort what it has already fetched. A post whose flagged comments straddle
 * a page boundary became two cards, and an older unanswered post on page 4 never
 * outranked a newer one on page 1. Both are properties of the unit.
 *
 * `since`/`last` bound the POST's own date, so an old post with a comment from this
 * morning bounds as old. Neither given applies no bound at all.
 *
 * ONE EDGE WORTH KNOWING: a relative `last` resolves against the top of the current
 * hour, and the cursor's filter hash covers the resolved instant — so a walk that
 * crosses an hour boundary answers 400 `cursor_filter_mismatch`. It is retryable by
 * reloading from the first page, which is the deliberate trade: a 400 a caller can see
 * beats a silently stale bound it cannot.
 */
/** ALL THREE OR NOTHING. A partial set would let a caller build a malformed post, so
 *  a trio with any part missing is read as unaddressable rather than half-usable. */
function toAddressing(w: AddressingWire | undefined): Addressing {
  const group = w?.group_external_id ?? null;
  const root = w?.root_external_id ?? null;
  const parent = w?.parent_external_id ?? null;
  const whole = !!group && !!root && !!parent;
  return {
    groupExternalId: whole ? group : null,
    rootExternalId: whole ? root : null,
    parentExternalId: whole ? parent : null,
  };
}

export async function fetchQueue(
  skoolId: number,
  bound: { since?: string | null; last?: string | null } = {},
  page: { limit?: number; cursor?: string | null; counts?: string[] } = {},
): Promise<QueuePage> {
  const body = await api().get<QueuePageWire>(
    `/v1/communities/${skoolId}/moderation/queue`,
    params({
      since: bound.since ?? null,
      last: bound.last ?? null,
      limit: page.limit ?? null,
      cursor: page.cursor ?? null,
      counts: page.counts?.length ? page.counts.join(',') : null,
    }),
  );
  return {
    items: body.items.map((w) => ({
      postId: w.post_id,
      postCreatedAt: w.post_created_at ?? null,
      url: w.url ?? null,
      postNeedsReply: w.post_needs_reply,
      addressing: toAddressing(w.addressing),
      waiting: (w.waiting ?? []).map((c) => ({
        commentId: c.comment_id,
        reason: c.reason as CommentReason,
        addressing: toAddressing(c.addressing),
      })),
    })),
    nextCursor: body.next_cursor,
    counts: body.counts ?? null,
    hasModerators: body.has_moderators,
    community: body.community,
  };
}

export interface ClearedItem {
  item: ModerationItem;
  cleared: boolean;
  /** The four sidebar counters, recomputed. Feed them to `applyBadges` — this is
   *  what removes the badge refetch after a write (§4.2, W2).
   *
   *  Null when the caller declined them (`badges: false`). A caller that keeps its own
   *  counters must not read this. */
  badges: Badges | null;
}

/**
 * `PATCH /v1/communities/{skool_id}/moderation/items/{kind}/{id}` — mark one item
 * answered, or restore it. `kind` and `id` are separate path segments, so nothing
 * parses a composite key, and the body carries only the mutable field.
 *
 * `window` reaches only the BADGES here (the item's own shape does not depend on a
 * time window), so pass the window the SIDEBAR counts over — the start of the
 * viewer's local day — not the window the page is filtered to. Omit it and the
 * badge is computed over the route's 90-day default, which is not the number the
 * sidebar is showing.
 *
 * `badges: false` DECLINES them (#173), and a caller that does not render them should.
 * They are a metrics sweep over the community, not a read of the row being written:
 * measured on prod, the same clear is 3.59s with them and 0.07s without. The Helper
 * panel keeps its own per-tab counters and threw this away on arrival, which made every
 * Skip pay for a number nothing displayed. `?include=` is the route's own vocabulary and
 * an empty value means "no blocks" — see `fetchInbox` on why '' is not the same as
 * omitting it.
 */
export async function clearInboxItem(
  skoolId: number,
  kind: InboxKind,
  id: number,
  cleared: boolean,
  window: { since?: string | null; last?: string | null; badges?: boolean } = {},
): Promise<ClearedItem> {
  const body = await api().send<ModerationItemWire>(
    'PATCH',
    `/v1/communities/${skoolId}/moderation/items/${kind}/${id}`,
    { cleared },
    params({
      since: window.since ?? null,
      last: window.last ?? null,
      include: window.badges === false ? '' : null,
    }),
  );
  return { item: toItem(body), cleared: body.cleared, badges: body.badges ?? null };
}

